import { collection, deleteDoc, doc, getDocs, writeBatch } from 'firebase/firestore'
import { db } from '../lib/db'
import { firestore, hasFirebase } from '../lib/firebase'
import type { Bookmark, QuizAttempt, QuizSession, SessionAnswer, SessionSnapshot } from '../types'

type QbankCollection = 'attempts' | 'bookmarks' | 'sessions' | 'snapshots' | 'answers'
type QbankTombstone = { id: string; userId: string; collection: QbankCollection; createdAt: string }

const COLLECTIONS: QbankCollection[] = ['attempts', 'bookmarks', 'sessions', 'snapshots', 'answers']
const PENDING_KEY = 'imed.qbank-sync-pending'
type PendingChange = { collection: QbankCollection; id: string }
type PendingState = { full?: boolean; changes: PendingChange[] }
let dailyTimer: number | undefined
const LAST_UPLOAD_KEY = 'imed.qbank-last-upload'

function ready(userId: string) { return Boolean(hasFirebase && firestore && userId) }
function safe<T>(value: T): T { return JSON.parse(JSON.stringify(value)) as T }
function cloudRef(userId: string, name: QbankCollection, id: string) {
  return doc(firestore!, 'users', userId, `qbank_${name}`, id)
}
function answerId(answer: SessionAnswer) { return `${answer.sessionId}:${answer.questionId}` }
function isQuestionBookmark(item: Bookmark) { return item.entityType === 'question' }
function pendingKey(userId: string) { return `${PENDING_KEY}:${userId}` }
function pendingFor(userId: string): PendingState {
  try { return JSON.parse(localStorage.getItem(pendingKey(userId)) || '{"changes":[]}') as PendingState } catch { return { changes: [] } }
}
function savePending(userId: string, pending: PendingState) { localStorage.setItem(pendingKey(userId), JSON.stringify(pending)); localStorage.setItem(PENDING_KEY, userId) }

async function localQbankData(userId: string) {
  const [attempts, bookmarks, sessions, tombstones] = await Promise.all([
    db.attempts.where('userId').equals(userId).toArray(),
    db.bookmarks.where('userId').equals(userId).toArray(),
    db.quizSessions.where('userId').equals(userId).toArray(),
    db.qbankSyncTombstones.where('userId').equals(userId).toArray() as Promise<QbankTombstone[]>,
  ])
  const ids = sessions.map((item) => item.id)
  const [snapshots, answerGroups] = await Promise.all([
    db.sessionSnapshots.bulkGet(ids),
    Promise.all(ids.map((sessionId) => db.sessionAnswers.where('sessionId').equals(sessionId).toArray())),
  ])
  return { attempts, bookmarks: bookmarks.filter(isQuestionBookmark), sessions, snapshots: snapshots.filter(Boolean) as SessionSnapshot[], answers: answerGroups.flat(), tombstones }
}

async function push(userId: string) {
  if (!ready(userId)) return
  const pending = pendingFor(userId)
  const data = pending.full ? await localQbankData(userId) : null
  const changes = new Map(pending.changes.map((item) => [`${item.collection}:${item.id}`, item]))
  const writes: Array<{ collection: QbankCollection; id: string; value: unknown }> = data
    ? [
        ...data.attempts.map((item) => ({ collection: 'attempts' as const, id: item.id, value: item })),
        ...data.bookmarks.map((item) => ({ collection: 'bookmarks' as const, id: item.id, value: item })),
        ...data.sessions.map((item) => ({ collection: 'sessions' as const, id: item.id, value: item })),
        ...data.snapshots.map((item) => ({ collection: 'snapshots' as const, id: item.sessionId, value: item })),
        ...data.answers.map((item) => ({ collection: 'answers' as const, id: answerId(item), value: item })),
      ]
    : (await Promise.all([...changes.values()].map(async (item) => {
        if (item.collection === 'attempts') { const value = await db.attempts.get(item.id); return value ? { ...item, value } : null }
        if (item.collection === 'bookmarks') { const value = await db.bookmarks.get(item.id); return value && isQuestionBookmark(value) ? { ...item, value } : null }
        if (item.collection === 'sessions') { const value = await db.quizSessions.get(item.id); return value ? { ...item, value } : null }
        if (item.collection === 'snapshots') { const value = await db.sessionSnapshots.get(item.id); return value ? { ...item, value } : null }
        const [sessionId, questionId] = item.id.split(':', 2)
        const value = await db.sessionAnswers.get([sessionId, questionId])
        return value ? { ...item, value } : null
      }))).filter(Boolean) as Array<{ collection: QbankCollection; id: string; value: unknown }>

  for (let offset = 0; offset < writes.length; offset += 400) {
    const batch = writeBatch(firestore!)
    for (const item of writes.slice(offset, offset + 400)) batch.set(cloudRef(userId, item.collection, item.id), safe(item.value))
    await batch.commit()
  }
  const tombstones = data?.tombstones ?? await db.qbankSyncTombstones.where('userId').equals(userId).toArray() as QbankTombstone[]
  for (const tombstone of tombstones) {
    await deleteDoc(cloudRef(userId, tombstone.collection, tombstone.id))
    await db.qbankSyncTombstones.delete(tombstone.id)
  }
  localStorage.removeItem(pendingKey(userId))
}

async function pull(userId: string) {
  if (!ready(userId)) return
  const results = await Promise.all(COLLECTIONS.map((name) => getDocs(collection(firestore!, 'users', userId, `qbank_${name}`))))
  const rows = Object.fromEntries(COLLECTIONS.map((name, index) => [name, results[index].docs.map((item) => item.data())])) as Record<QbankCollection, Record<string, unknown>[]>
  const attempts = rows.attempts as unknown as QuizAttempt[]
  const bookmarks = rows.bookmarks as unknown as Bookmark[]
  const sessions = rows.sessions as unknown as QuizSession[]
  const snapshots = rows.snapshots as unknown as SessionSnapshot[]
  const answers = rows.answers as unknown as SessionAnswer[]
  await db.transaction('rw', [db.attempts, db.bookmarks, db.quizSessions, db.sessionSnapshots, db.sessionAnswers], async () => {
    if (attempts.length) await db.attempts.bulkPut(attempts.filter((item) => item.userId === userId))
    if (bookmarks.length) await db.bookmarks.bulkPut(bookmarks.filter((item) => item.userId === userId && isQuestionBookmark(item)))
    if (sessions.length) await db.quizSessions.bulkPut(sessions.filter((item) => item.userId === userId))
    if (snapshots.length) await db.sessionSnapshots.bulkPut(snapshots)
    if (answers.length) await db.sessionAnswers.bulkPut(answers)
  })
}

export const firebaseQbankSync = {
  enabled: () => hasFirebase,
  queue(userId: string) {
    this.markAll(userId)
  },
  mark(userId: string, collection: QbankCollection, id: string) {
    if (!ready(userId)) return
    const pending = pendingFor(userId)
    if (!pending.full && !pending.changes.some((item) => item.collection === collection && item.id === id)) pending.changes.push({ collection, id })
    savePending(userId, pending)
  },
  markAll(userId: string) {
    if (!ready(userId)) return
    savePending(userId, { full: true, changes: [] })
  },
  async upload(userId: string) {
    if (!ready(userId) || !navigator.onLine) return
    await push(userId)
    localStorage.removeItem(PENDING_KEY)
    localStorage.setItem(`${LAST_UPLOAD_KEY}:${userId}`, new Date().toISOString())
    window.dispatchEvent(new CustomEvent('imed:qbank-uploaded'))
  },
  async download(userId: string) {
    if (!ready(userId) || !navigator.onLine) return
    await pull(userId)
    window.dispatchEvent(new CustomEvent('imed:qbank-downloaded'))
  },
  async syncNow(userId: string) {
    await this.upload(userId)
    await this.download(userId)
    window.dispatchEvent(new CustomEvent('imed:qbank-synced'))
  },
  async resume() { /* QBank progress is intentionally manual/daily, never synced on startup. */ },
  scheduleDaily(userId: string) {
    if (!ready(userId)) return
    if (dailyTimer) clearTimeout(dailyTimer)
    const lastUpload = Date.parse(localStorage.getItem(`${LAST_UPLOAD_KEY}:${userId}`) || '')
    const nextUpload = Number.isFinite(lastUpload) ? lastUpload + 24 * 60 * 60 * 1000 : Date.now() + 24 * 60 * 60 * 1000
    dailyTimer = window.setTimeout(async () => {
      try { if (localStorage.getItem(pendingKey(userId))) await this.upload(userId) }
      finally { this.scheduleDaily(userId) }
    }, Math.max(1_000, nextUpload - Date.now()))
  },
  async remove(userId: string, collection: QbankCollection, id: string) {
    await db.qbankSyncTombstones.put({ id: `${collection}:${id}`, userId, collection, createdAt: new Date().toISOString() })
    // The tombstone itself is enough to perform the remote deletion. Do not
    // turn a single deletion into a full-history upload.
    savePending(userId, pendingFor(userId))
  },
  async migrateLegacyProgress(legacyUserId: string | null, userId: string) {
    if (!legacyUserId || legacyUserId === userId || localStorage.getItem(`imed.qbank-migrated:${userId}`)) return
    const [attempts, sessions, bookmarks] = await Promise.all([
      db.attempts.where('userId').equals(legacyUserId).toArray(),
      db.quizSessions.where('userId').equals(legacyUserId).toArray(),
      db.bookmarks.where('userId').equals(legacyUserId).toArray(),
    ])
    await db.transaction('rw', [db.attempts, db.quizSessions, db.bookmarks], async () => {
      if (attempts.length) await db.attempts.bulkPut(attempts.map((item) => ({ ...item, userId })))
      if (sessions.length) await db.quizSessions.bulkPut(sessions.map((item) => ({ ...item, userId })))
      const questionBookmarks = bookmarks.filter(isQuestionBookmark)
      if (questionBookmarks.length) await db.bookmarks.bulkPut(questionBookmarks.map((item) => ({ ...item, userId })))
    })
    localStorage.setItem(`imed.qbank-migrated:${userId}`, 'true')
    this.markAll(userId)
  },
}
